<script>
import { ref } from 'vue';
import { mapGetters } from 'vuex';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useConversationLabels } from 'dashboard/composables/useConversationLabels';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import Spinner from 'shared/components/Spinner.vue';
import LabelDropdown from 'shared/components/ui/label/LabelDropdown.vue';
import AddLabel from 'shared/components/ui/dropdown/AddLabel.vue';
import {
  isSupportXPrimaryCategory,
  primaryCategoryDisplayName,
  replacePrimaryCategory,
} from 'dashboard/helper/supportXPrimaryCategory';

export default {
  components: {
    Spinner,
    LabelDropdown,
    AddLabel,
  },
  setup() {
    const { isAdmin } = useAdmin();

    const {
      savedLabels,
      activeLabels,
      accountLabels,
      addLabelToConversation,
      removeLabelFromConversation,
      onUpdateLabels,
    } = useConversationLabels();

    const showSearchDropdownLabel = ref(false);

    const toggleLabels = () => {
      showSearchDropdownLabel.value = !showSearchDropdownLabel.value;
    };

    const closeDropdownLabel = () => {
      showSearchDropdownLabel.value = false;
    };

    const keyboardEvents = {
      KeyL: {
        action: e => {
          e.preventDefault();
          toggleLabels();
        },
      },
      Escape: {
        action: () => {
          if (showSearchDropdownLabel.value) {
            toggleLabels();
          }
        },
        allowOnFocusedInput: true,
      },
    };
    useKeyboardEvents(keyboardEvents);
    return {
      isAdmin,
      savedLabels,
      activeLabels,
      accountLabels,
      addLabelToConversation,
      removeLabelFromConversation,
      onUpdateLabels,
      showSearchDropdownLabel,
      closeDropdownLabel,
      toggleLabels,
    };
  },
  data() {
    return {
      selectedLabels: [],
      showCategoryDropdown: false,
    };
  },

  computed: {
    ...mapGetters({
      conversationUiFlags: 'conversationLabels/getUIFlags',
    }),
    categoryLabels() {
      return this.accountLabels.filter(isSupportXPrimaryCategory);
    },
    ordinaryAccountLabels() {
      return this.accountLabels.filter(
        label => !isSupportXPrimaryCategory(label)
      );
    },
    ordinarySavedLabels() {
      const categoryTitles = new Set(
        this.categoryLabels.map(label => label.title)
      );
      return this.savedLabels.filter(title => !categoryTitles.has(title));
    },
    ordinaryActiveLabels() {
      return this.activeLabels.filter(
        label => !isSupportXPrimaryCategory(label)
      );
    },
    selectedCategories() {
      return this.categoryLabels.filter(label =>
        this.savedLabels.includes(label.title)
      );
    },
    categoryButtonText() {
      if (this.selectedCategories.length > 1) return 'Multiple categories';
      if (this.selectedCategories.length === 1) {
        return primaryCategoryDisplayName(this.selectedCategories[0].title);
      }
      return 'Select category';
    },
  },
  methods: {
    primaryCategoryDisplayName,
    toggleCategoryDropdown() {
      this.showCategoryDropdown = !this.showCategoryDropdown;
    },
    closeCategoryDropdown() {
      this.showCategoryDropdown = false;
    },
    async selectCategory(category) {
      await this.onUpdateLabels(
        replacePrimaryCategory({
          savedLabels: this.savedLabels,
          accountLabels: this.accountLabels,
          category: category.title,
        })
      );
      this.closeCategoryDropdown();
    },
  },
};
</script>

<template>
  <div class="sidebar-labels-wrap">
    <div
      v-if="!conversationUiFlags.isFetching"
      class="contact-conversation--list"
    >
      <div
        v-if="categoryLabels.length"
        v-on-clickaway="closeCategoryDropdown"
        class="relative mb-3"
      >
        <span class="block mb-1.5 text-xs font-medium text-n-slate-11">
          Category
        </span>
        <button
          type="button"
          class="flex items-center justify-between w-full px-3 py-2 text-sm text-left border rounded-lg bg-n-background dark:bg-n-solid-3"
          :class="
            selectedCategories.length > 1
              ? 'border-n-amber-7 text-n-amber-12'
              : 'border-n-weak text-n-slate-12'
          "
          aria-haspopup="listbox"
          :aria-expanded="showCategoryDropdown"
          @click="toggleCategoryDropdown"
        >
          <span class="flex items-center min-w-0 gap-2">
            <span
              v-if="selectedCategories.length === 1"
              class="flex-none w-2.5 h-2.5 rounded-full"
              :style="{ backgroundColor: selectedCategories[0].color }"
            />
            <span class="truncate">{{ categoryButtonText }}</span>
          </span>
          <fluent-icon icon="chevron-down" size="16" />
        </button>
        <div
          v-if="showCategoryDropdown"
          class="absolute left-0 right-0 z-[10000] p-1.5 mt-1 overflow-y-auto border rounded-lg shadow-lg top-full max-h-72 border-n-strong bg-n-alpha-3 backdrop-blur-[100px]"
          role="listbox"
        >
          <button
            v-for="category in categoryLabels"
            :key="category.id"
            type="button"
            class="flex items-center w-full gap-2.5 px-2.5 py-2 text-sm text-left rounded-md text-n-slate-12 hover:bg-n-slate-3 dark:hover:bg-n-solid-3"
            :class="{
              'bg-n-slate-3 dark:bg-n-solid-3': savedLabels.includes(
                category.title
              ),
            }"
            role="option"
            :aria-selected="savedLabels.includes(category.title)"
            @click="selectCategory(category)"
          >
            <span
              class="flex-none w-2.5 h-2.5 rounded-full"
              :style="{ backgroundColor: category.color }"
            />
            <span>{{ primaryCategoryDisplayName(category.title) }}</span>
            <fluent-icon
              v-if="savedLabels.includes(category.title)"
              icon="checkmark"
              size="16"
              class="ml-auto"
            />
          </button>
        </div>
      </div>
      <div
        v-on-clickaway="closeDropdownLabel"
        class="label-wrap flex flex-wrap"
        @keyup.esc="closeDropdownLabel"
      >
        <AddLabel @add="toggleLabels" />
        <woot-label
          v-for="label in ordinaryActiveLabels"
          :key="label.id"
          :title="label.title"
          :description="label.description"
          show-close
          :color="label.color"
          variant="smooth"
          class="max-w-[calc(100%-0.5rem)]"
          @remove="removeLabelFromConversation"
        />

        <div
          :class="{
            'block visible': showSearchDropdownLabel,
            'hidden invisible': !showSearchDropdownLabel,
          }"
          class="border rounded-lg bg-n-alpha-3 top-6 backdrop-blur-[100px] absolute w-full shadow-lg border-n-strong dark:border-n-strong p-2 box-border z-[9999]"
        >
          <LabelDropdown
            v-if="showSearchDropdownLabel"
            :account-labels="ordinaryAccountLabels"
            :selected-labels="ordinarySavedLabels"
            :allow-creation="isAdmin"
            @add="addLabelToConversation"
            @remove="removeLabelFromConversation"
          />
        </div>
      </div>
    </div>
    <Spinner v-else />
  </div>
</template>

<style lang="scss" scoped>
.sidebar-labels-wrap {
  margin-bottom: 0;
}
.contact-conversation--list {
  width: 100%;

  .label-wrap {
    line-height: 1.5rem;
    position: relative;
  }
}
</style>
